/**
 * Phase 25b · Cart context.
 *
 * - Persists asset_ids in localStorage under `gls_cart_asset_ids`.
 * - Syncs across tabs via `storage` event.
 * - Auto-fires `POST /api/upsell/context/action-attempted` with
 *   {action: "cart_size_2"|"cart_size_4"} to trigger the bundle_upsell rule.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "gls_cart_asset_ids";
const API = process.env.REACT_APP_BACKEND_URL || "";

const CartContext = createContext(null);

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function writeStorage(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {}
}

async function pingUpsell(action) {
  try {
    await fetch(`${API}/api/upsell/context/action-attempted`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, surface: "marketplace" }),
    });
  } catch {}
}

export function CartProvider({ children }) {
  const [ids, setIds] = useState(readStorage);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setIds(readStorage());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Fire bundle_upsell trigger on threshold changes
  useEffect(() => {
    if (ids.length === 2) pingUpsell("cart_size_2");
    if (ids.length === 4) pingUpsell("cart_size_4");
  }, [ids.length]);

  const add = useCallback((id) => {
    if (!id) return;
    setIds((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      writeStorage(next);
      return next;
    });
  }, []);

  const remove = useCallback((id) => {
    setIds((prev) => {
      const next = prev.filter((x) => x !== id);
      writeStorage(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    writeStorage([]);
    setIds([]);
  }, []);

  const has = useCallback((id) => ids.includes(id), [ids]);

  return (
    <CartContext.Provider value={{ ids, count: ids.length, add, remove, clear, has }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    // Safe fallback when provider is missing (SSR / test)
    return { ids: [], count: 0, add: () => {}, remove: () => {}, clear: () => {}, has: () => false };
  }
  return ctx;
}

export default CartContext;
